import { AsyncPipe, DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { Observable } from 'rxjs';
import { Tenant } from '../../Models/tenant';
import { ToastMessageType, ToastService } from '../../Common';
import { MaintenanceTaskSchedulerDataService } from '../maintenance-task-scheduler-data.service';
import { RecurringScheduledMaintenanceTaskSeriesSummary } from '../scheduled-maintenance-recurrence.model';

@Component({
    selector: 'maintenance-series-detail',
    templateUrl: './maintenance-series-detail.component.html',
    styleUrls: ['./maintenance-series-detail.component.less'],
    imports: [DatePipe, MatButton, AsyncPipe]
})
export class MaintenanceSeriesDetailComponent {
    @Input() series: RecurringScheduledMaintenanceTaskSeriesSummary;
    @Input() allTenants$: Observable<Tenant[]>;
    @Output() changed = new EventEmitter<void>();

    constructor(
        private readonly dataService: MaintenanceTaskSchedulerDataService,
        private readonly toastService: ToastService) { }

    pause(): void {
        this.dataService.pauseRecurringScheduledMaintenanceTaskSeries(this.series.Id)
            .subscribe(
                () => this.changed.emit(),
                (response) => this.showError(response));
    }

    resume(): void {
        this.dataService.resumeRecurringScheduledMaintenanceTaskSeries(this.series.Id)
            .subscribe(
                () => this.changed.emit(),
                (response) => this.showError(response));
    }

    cancel(): void {
        this.dataService.cancelRecurringScheduledMaintenanceTaskSeries(this.series.Id)
            .subscribe(
                () => this.changed.emit(),
                (response) => this.showError(response));
    }

    getRule(): string {
        if (!this.series) {
            return '';
        }

        if (this.series.RecurrenceKind === 'Weekly' && this.series.Weekdays?.length) {
            return `${this.series.RecurrenceKind} every ${this.series.Interval} on ${this.series.Weekdays.join(', ')}`;
        }

        if (this.series.RecurrenceKind === 'Monthly' && this.series.DayOfMonth) {
            return `${this.series.RecurrenceKind} every ${this.series.Interval} on day ${this.series.DayOfMonth}`;
        }

        return `${this.series.RecurrenceKind} every ${this.series.Interval}`;
    }

    getSelectedTenantNames(allTenants: Tenant[] | null): string {
        const tenantIds = this.series?.TenantIds || [];
        const tenantNames = tenantIds.map(tenantId => {
            const tenant = allTenants?.find(t => t.TenantId?.toLowerCase() === tenantId.toLowerCase());
            return tenant?.Text || tenantId;
        });

        return tenantNames.length ? tenantNames.join(', ') : 'Selected tenants';
    }

    private showError(response: any): void {
        this.toastService.sendMessage('Error updating recurring schedule', this.errorMessage(response), ToastMessageType.error);
    }

    private errorMessage(response: any): string {
        if (typeof response?.error === 'string') {
            return response.error;
        }

        return response?.error?.Message || response?.error?.message || response?.message || 'Unexpected error';
    }
}
